import { Validators } from '@biteinc/common';
import {
  allEnumValues,
  BiteAdminMfaOption,
  FulfillmentMethod,
  FulfillmentMethodHelper,
  LocationState,
  ModelType,
  OrderChannel,
  UrlTarget,
} from '@biteinc/enums';
import { orgSchema } from '@biteinc/schemas';

app.Org = app.AbstractModel.extend({
  ModelName: 'org',
  Schema: orgSchema,
  Type: ModelType.Org,

  initialize() {
    app.AbstractModel.prototype.initialize.apply(this, arguments);

    const liveI9nSchemaBySystem = {};
    const nonLiveI9nSchemaBySystem = {};
    this.locations = (this.get('locations') || []).map((locationJson) => {
      return new app.Location(locationJson);
    });

    // First add the live integrations
    this.locations.forEach((location) => {
      if (location.isLive()) {
        location.getAllI9nSchemas().forEach((i9nSchema) => {
          liveI9nSchemaBySystem[i9nSchema.system] = i9nSchema;
        });
      }
    });

    // Then add all the others
    this.locations.forEach((location) => {
      if (!location.isLive()) {
        location.getAllI9nSchemas().forEach((i9nSchema) => {
          if (!liveI9nSchemaBySystem[i9nSchema.system]) {
            nonLiveI9nSchemaBySystem[i9nSchema.system] = i9nSchema;
          }
        });
      }
    });

    this.liveI9nSchemas = Object.values(liveI9nSchemaBySystem);
    this.nonLiveI9nSchemas = Object.values(nonLiveI9nSchemaBySystem);
    this.liveLocationCount = this.locations.reduce((acc, location) => {
      return acc + (location.isLive() ? 1 : 0);
    }, 0);
  },

  canBeDestroyed() {
    return false;
  },

  url() {
    return `/api/orgs/${this.id || ''}`;
  },

  bureauUrl() {
    return `/${this.get('normalizedName')}`;
  },

  hasIntegrationWithSystem(system) {
    return app.locationList.some((location) => {
      return location.hasIntegrationWithSystem(system);
    });
  },

  getFieldCollection(field, subProperty, _includeAllValues, _keyModel) {
    const fieldName = (subProperty ? `${subProperty}.` : '') + field;
    switch (fieldName) {
      case 'appearanceId':
        return app.orgAppearanceList;
      case 'enterpriseTagId':
        return app.enterpriseTagList;
      case 'sourceOrgId':
        return app.orgList;
      case 'biteAdminMfaOptions':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: BiteAdminMfaOption,
          nameGenerator: (biteAdminMfaOption) => {
            switch (biteAdminMfaOption) {
              case BiteAdminMfaOption.Email:
                return 'Email';
            }
          },
        });
      case 'webFulfillmentMethods':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: FulfillmentMethod,
          nameGenerator: FulfillmentMethodHelper.name,
          values: allEnumValues(FulfillmentMethod).filter((fulfillmentMethod) => {
            return FulfillmentMethodHelper.orderChannel(fulfillmentMethod) === OrderChannel.Web;
          }),
        });
      case 'customCssBuildId':
        return app.customCssGcnBuildList;
      case 'customCssVitrineBuildId':
        return app.customCssVitrineBuildList;
      case 'mobileAppSidebarLinks.target':
      case 'mobileAppPolicyLinks.target':
        return app.AbstractCollection.createFromTsEnum({
          tsEnum: UrlTarget,
          nameGenerator: (urlTarget) => {
            switch (urlTarget) {
              case UrlTarget.ExternalBrowser:
                return 'External browser';
              case UrlTarget.InternalBrowser:
                return 'Internal browser';
              case UrlTarget.ShareDialog:
                return 'Share dialog';
            }
          },
        });
    }
    return null;
  },

  validateField(field, value, callback) {
    switch (field) {
      case 'brandColor': {
        const passes = new RegExp(Validators.hexColorWithOptionalOpacityRegexString, 'i').test(
          value,
        );
        if (!passes) {
          callback(
            false,
            'Please use RGB(A) format: #FF0000 for red or #FF00007F for red with 50% alpha.',
          );
          return false;
        }
        callback(true);
        break;
      }
      default:
        callback(true);
    }
    return true;
  },

  getLocationsThatMatchQuery(query, includeOnlyLiveLocations) {
    const specialTokens = query
      .toLowerCase()
      .split(' ')
      .filter((token) => {
        return token.charAt(0) === ':';
      });
    return this.locations.filter((location) => {
      if (includeOnlyLiveLocations && location.state !== LocationState.Live) {
        return false;
      }
      return specialTokens.every((token) => {
        return location.matchesQuery(token);
      });
    });
  },

  /**
   * @param {string} query
   * @returns {boolean}
   */
  matchesQuery(query /* optDisplayName */) {
    if (!query) {
      return true;
    }

    if (this.locations.length) {
      const matchesAllTokens = this.getLocationsThatMatchQuery(query, false).length > 0;
      if (!matchesAllTokens) {
        return false;
      }
    } else {
      // The tokens only apply to the locations query, so if the org has no locations, then any
      // query with a token should not match
      const hasTokens = !!query.split(' ').find((token) => {
        return token.charAt(0) === ':';
      });
      if (hasTokens) {
        return false;
      }
    }

    const tokenlessQuery = query
      .split(' ')
      .filter((token) => {
        return token.charAt(0) !== ':';
      })
      .join(' ');

    const orgMatchesQuery = app.AbstractModel.prototype.matchesQuery.apply(this, [tokenlessQuery]);
    if (orgMatchesQuery) {
      return true;
    }

    // Also match against location names
    return this.locations.some((location) => location.matchesQuery(tokenlessQuery));
  },

  isSprinkles() {
    return this.id === '5f340b7ea47803001ed17fd7';
  },

  refreshMenuCaches(options) {
    const url = `${this.url()}/refresh-menu-caches`;
    app.makeRequest('POST', url, null, options.success, options.error);
  },

  removeArchive(options) {
    const url = `${this.url()}/archive`;
    app.makeRequest('DELETE', url, null, options.success, options.error);
  },

  hasMdmSettingsForProvider(mdmProvider) {
    return this.get('mdmSettings')?.some((mdmSettings) => {
      return mdmSettings.mdmProvider === mdmProvider;
    });
  },
});
